import { useMemo, useState } from "react";
import { cx } from "../lib/format";
import { Icon } from "./Icon";

interface DiffFile {
  path: string;
  header: string[];
  lines: string[];
  adds: number;
  dels: number;
  binary: boolean;
}

export function parseUnifiedDiff(diff: string): DiffFile[] {
  const files: DiffFile[] = [];
  let cur: DiffFile | null = null;
  let inHunk = false;
  for (const line of diff.split("\n")) {
    if (line.startsWith("diff --git ")) {
      const m = line.match(/^diff --git a\/(.+?) b\/(.+)$/);
      cur = { path: m ? m[2] : line.slice(11), header: [line], lines: [], adds: 0, dels: 0, binary: false };
      files.push(cur);
      inHunk = false;
      continue;
    }
    if (!cur) {
      // Diff without "diff --git" headers (plain unified diff).
      if (line.startsWith("--- ")) {
        cur = { path: "", header: [line], lines: [], adds: 0, dels: 0, binary: false };
        files.push(cur);
      }
      continue;
    }
    if (!inHunk) {
      if (line.startsWith("@@")) {
        inHunk = true;
        cur.lines.push(line);
      } else {
        cur.header.push(line);
        if (line.startsWith("+++ ") && !line.startsWith("+++ /dev/null")) cur.path = line.slice(4).replace(/^b\//, "");
        if (line.startsWith("--- ") && !cur.path) cur.path = line.slice(4).replace(/^a\//, "");
        if (line.startsWith("Binary files")) cur.binary = true;
      }
      continue;
    }
    if (line.startsWith("--- ") && cur.lines.length && files.length && !cur.header[0].startsWith("diff --git")) {
      cur = { path: "", header: [line], lines: [], adds: 0, dels: 0, binary: false };
      files.push(cur);
      inHunk = false;
      continue;
    }
    cur.lines.push(line);
    if (line.startsWith("+")) cur.adds++;
    else if (line.startsWith("-")) cur.dels++;
  }
  // Drop the trailing empty line produced by a final newline.
  for (const f of files) while (f.lines.length && f.lines[f.lines.length - 1] === "") f.lines.pop();
  return files;
}

export function DiffView({ diff }: { diff: string }) {
  const files = useMemo(() => parseUnifiedDiff(diff), [diff]);
  const total = files.reduce((n, f) => n + f.lines.length, 0);
  if (!files.length) return <pre className="diff-raw">{diff || "(empty diff)"}</pre>;
  return (
    <div className="diff">
      {files.map((f, i) => (
        <DiffFileView key={`${f.path}-${i}`} file={f} defaultOpen={total < 1500 || f.lines.length < 200} />
      ))}
    </div>
  );
}

function DiffFileView({ file, defaultOpen }: { file: DiffFile; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="diff-file">
      <button className="diff-file-header" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={14} />
        <span className="diff-path">{file.path || "(unknown file)"}</span>
        <span className="diff-counts">
          <span className="diff-add-count">+{file.adds}</span> <span className="diff-del-count">−{file.dels}</span>
        </span>
      </button>
      {open && (
        <div className="diff-body">
          {file.binary && <div className="diff-line diff-meta">Binary file</div>}
          {file.lines.map((l, i) => (
            <div
              key={i}
              className={cx(
                "diff-line",
                l.startsWith("@@") && "diff-hunk",
                l.startsWith("+") && "diff-add",
                l.startsWith("-") && "diff-del",
                l.startsWith("\\") && "diff-meta",
              )}
            >
              {l || " "}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
